
// Define the output structure for the speakability analysis
export interface SpeakabilityBreakdown {
  fluency: number;
  familiarity: number;
  simplicity: number;
  total: number;
  bonus?: number;
  fluencyEarned?: string[];
  familiarityEarned?: string[];
  simplicityEarned?: string[];
  bonusEarned?: string[];
  words?: string[];
}

// Basic constants for phonetic analysis
const VOWELS = 'aeiouy';
const CONSONANTS = 'bcdfghjklmnpqrstvwxz';
const HARSH_ENDINGS = ['rk', 'xt', 'pt', 'lf', 'rd', 'mp', 'ct', 'ft', 'sp', 'sk', 'lk', 'lm', 'nk'];
const DIFFICULT_CLUSTERS = ['scr', 'spr', 'str', 'spl', 'thr', 'sch', 'shr', 'thw'];
const AMBIGUOUS_SOUNDS = ['ough', 'augh', 'ea', 'ie', 'ei', 'gh', 'ph', 'th', 'ch', 'sh', 'wh'];
const CONFUSING_DOUBLES = ['ss', 'tt', 'cc', 'll', 'pp', 'mm', 'nn', 'ff', 'dd', 'rr'];
const SILENT_ENDINGS = ['e', 'h', 'b', 'g', 'n'];
const UNCOMMON_PHONEMES = ['zh', 'ts', 'dj', 'tl', 'tn', 'qu'];
const DIFFICULT_VOWEL_CLUSTERS = ['uoi', 'eaa', 'ii', 'yy', 'aoe', 'uie'];

/**
 * Split domain into words based on common separators and patterns
 */
function splitWords(domain: string): string[] {
  // Remove TLD extensions like .com, .org, etc.
  const cleaned = domain.replace(/\.[a-z]{2,}$/i, '');
  
  // Split camelCase (e.g., DreamMakers -> Dream Makers)
  const withSpaces = cleaned.replace(/([a-z])([A-Z])/g, '$1 $2');
  
  // Split on common separators and filter out empty strings
  return withSpaces.split(/[\s\-_.]/).filter(Boolean);
}

/**
 * Count syllables in a word or phrase
 * Uses a simple vowel-group counting algorithm
 */
function countSyllables(text: string): number {
  // Handle empty strings
  if (!text || text.length === 0) return 0;
  
  // Clean up the text - only letters matter for syllable counting
  const cleanText = text.toLowerCase().replace(/[^a-z]/g, '');
  if (cleanText.length === 0) return 0;
  
  let count = 0;
  let prevIsVowel = false;
  
  // Count vowel groups (each vowel group = 1 syllable)
  for (let i = 0; i < cleanText.length; i++) {
    const isVowel = VOWELS.includes(cleanText[i]);
    if (isVowel && !prevIsVowel) {
      count++;
    }
    prevIsVowel = isVowel;
  }
  
  // Adjust for silent 'e' at the end
  if (cleanText.endsWith('e') && count > 1 && !cleanText.endsWith('le')) {
    count--;
  }
  
  // Adjust for 'le' ending (actually adds a syllable)
  if (cleanText.endsWith('le') && cleanText.length > 2 && !VOWELS.includes(cleanText[cleanText.length - 3])) {
    count++;
  }
  
  // Ensure at least one syllable for any non-empty text
  return Math.max(count, 1);
}

/**
 * Calculate fluency score (0-40)
 * Measures how smoothly the name flows when spoken
 */
function calculateFluencyPure(word: string): { score: number, earned: string[] } {
  let score = 0;
  const earned: string[] = [];
  
  const lower = word.toLowerCase();
  
  // +10 if word has different starting consonant than ending (avoids tongue-twisters)
  if (lower[0] !== lower[lower.length - 1] || VOWELS.includes(lower[0])) {
    score += 10;
    earned.push("Different start/end consonants (+10)");
  }
  
  // +10 if total syllables between 1-3
  const totalSyllables = countSyllables(word);
  if (totalSyllables >= 1 && totalSyllables <= 3) {
    score += 10;
    earned.push(`Optimal syllable count: ${totalSyllables} (+10)`);
  }
  
  // +10 if no harsh consonant endings
  const noHarshEndings = !HARSH_ENDINGS.some(ending => lower.endsWith(ending));
  if (noHarshEndings) {
    score += 10;
    earned.push("No harsh consonant endings (+10)");
  }
  
  // +5 if no difficult vowel clusters
  if (!DIFFICULT_VOWEL_CLUSTERS.some(cluster => lower.includes(cluster))) {
    score += 5;
    earned.push("Natural vowel clusters (+5)");
  }
  
  // +5 if no uncommon phoneme combinations
  if (!UNCOMMON_PHONEMES.some(phoneme => lower.includes(phoneme))) {
    score += 5;
    earned.push("Common phoneme patterns (+5)");
  }
  
  return { score: Math.min(score, 40), earned };
}

/**
 * Calculate familiarity score (0-30)
 * Based on phonetic patterns that feel familiar to speakers
 */
function calculateFamiliarityPure(word: string): { score: number, earned: string[] } {
  let score = 0;
  const earned: string[] = [];
  
  const lower = word.toLowerCase();
  
  // +10 if word follows balanced consonant-vowel patterns
  // Test for CVC, CV, VC patterns 
  const hasSimplePattern = /^[^aeiouy]?[aeiouy][^aeiouy]?$/i.test(lower);
  
  // Test for alternating consonants and vowels
  let alternations = 0;
  let prevIsVowel = VOWELS.includes(lower[0]);
  
  for (let i = 1; i < lower.length; i++) {
    const isVowel = VOWELS.includes(lower[i]);
    if (isVowel !== prevIsVowel) {
      alternations++;
    }
    prevIsVowel = isVowel;
  }
  
  // Words with good alternation will have alternations ≥ word.length/2
  if (hasSimplePattern || alternations >= (lower.length / 2) - 1) {
    score += 10;
    earned.push("Balanced consonant-vowel pattern (+10)");
  }
  
  // +10 if vowel-to-consonant ratio is balanced (35-65%)
  if (lower.length > 0) {
    const vowelsCount = [...lower].filter(c => VOWELS.includes(c)).length;
    const ratio = vowelsCount / lower.length;
    
    if (ratio >= 0.35 && ratio <= 0.65) {
      score += 10;
      earned.push("Balanced vowel-consonant ratio (+10)");
    }
  }
  
  // +5 if syllables end in vowels (easier to hear/spell)
  if (lower.length > 0 && VOWELS.includes(lower[lower.length - 1])) {
    score += 5;
    earned.push("Ends with a vowel sound (+5)");
  }
  
  // +5 if all characters are standard a-z (no special characters or numbers)
  if (/^[a-z]+$/i.test(word)) {
    score += 5;
    earned.push("Standard character set (+5)");
  }
  
  return { score: Math.min(score, 30), earned };
}

/**
 * Calculate simplicity score (0-30)
 * Evaluates how easy the name is to hear and spell correctly
 */
function calculateSimplicityPure(word: string): { score: number, earned: string[] } {
  let score = 0;
  const earned: string[] = [];
  
  const lower = word.toLowerCase();
  
  // +10 if word has 1-3 syllables
  const syllables = countSyllables(word);
  if (syllables >= 1 && syllables <= 3) {
    score += 10;
    earned.push(`Simple syllable count: ${syllables} (+10)`);
  }
  
  // +10 if no confusing double letters
  const noConfusingDoubles = !CONFUSING_DOUBLES.some(double => lower.includes(double));
  if (noConfusingDoubles) {
    score += 10;
    earned.push("No confusing double letters (+10)");
  }
  
  // +5 if no ambiguous phonetic combinations
  const noAmbiguousSounds = !AMBIGUOUS_SOUNDS.some(sound => lower.includes(sound));
  if (noAmbiguousSounds) {
    score += 5;
    earned.push("No ambiguous sound patterns (+5)");
  }
  
  // +5 if consistent capitalization pattern (no midword capital)
  if (!/[a-z][A-Z]/.test(word)) {
    score += 5;
    earned.push("Consistent capitalization (+5)");
  }
  
  return { score: Math.min(score, 30), earned };
}

/**
 * Calculate bonus points (0-10)
 * Special bonuses for memorable characteristics
 */
function calculateBonusSpeakability(word: string): { score: number, earned: string[] } {
  let score = 0;
  const earned: string[] = [];
  
  const lower = word.toLowerCase();
  
  // +5 if the name is palindromic or semi-palindromic
  if (lower.length >= 3 && lower === lower.split('').reverse().join('')) {
    score += 5;
    earned.push("Palindromic word pattern (+5)");
  }
  
  // +5 if total characters = total syllables × 3-4 (suggests balance)
  const syll = countSyllables(word);
  const ratio = word.length / (syll || 1);
  if (ratio >= 2.5 && ratio <= 4.5) {
    score += 5;
    earned.push(`Balanced length/syllable ratio: ${ratio.toFixed(1)} (+5)`);
  }
  
  return { score: Math.min(score, 10), earned };
}

/**
 * Calculate the overall speakability score for a single word
 */
function calculateWordSpeakabilityScore(word: string): SpeakabilityBreakdown {
  // Calculate individual scores
  const fluencyResult = calculateFluencyPure(word);
  const familiarityResult = calculateFamiliarityPure(word);
  const simplicityResult = calculateSimplicityPure(word);
  const bonusResult = calculateBonusSpeakability(word);
  
  // Sum the scores for total
  const total = Math.min(100, fluencyResult.score + familiarityResult.score + simplicityResult.score + bonusResult.score);
  
  return {
    fluency: fluencyResult.score,
    familiarity: familiarityResult.score,
    simplicity: simplicityResult.score,
    bonus: bonusResult.score,
    total,
    fluencyEarned: fluencyResult.earned,
    familiarityEarned: familiarityResult.earned,
    simplicityEarned: simplicityResult.earned,
    bonusEarned: bonusResult.earned
  };
}

/**
 * Calculate the overall speakability score
 */
export function calculateSpeakabilityScore(domain: string): SpeakabilityBreakdown {
  // Split domain into words
  const words = splitWords(domain);
  
  // If empty input, return zeros
  if (words.length === 0) {
    return {
      fluency: 0,
      familiarity: 0,
      simplicity: 0,
      total: 0,
      words: []
    };
  }
  
  // For single-word domains
  if (words.length === 1) {
    const result = calculateWordSpeakabilityScore(words[0]);
    result.words = words;
    
    // Bonus for short + clean single word domains
    if (words[0].length <= 5 && /^[a-z]+$/i.test(words[0])) {
      if (!result.bonusEarned) result.bonusEarned = [];
      result.bonusEarned.push("Short single-word domain (+5)");
      result.bonus = (result.bonus || 0) + 5;
      result.total = Math.min(100, result.total + 5);
    }
    
    return result;
  }
  
  // For multi-word domains
  let totalFluency = 0;
  let totalFamiliarity = 0;
  let totalSimplicity = 0;
  let totalBonus = 0;
  
  const fluencyEarned: string[] = [];
  const familiarityEarned: string[] = [];
  const simplicityEarned: string[] = [];
  const bonusEarned: string[] = [];
  
  // Calculate scores for each word
  for (const word of words) {
    const wordScore = calculateWordSpeakabilityScore(word);
    
    totalFluency += wordScore.fluency;
    totalFamiliarity += wordScore.familiarity;
    totalSimplicity += wordScore.simplicity;
    totalBonus += wordScore.bonus || 0;
    
    if (wordScore.fluencyEarned) fluencyEarned.push(...wordScore.fluencyEarned);
    if (wordScore.familiarityEarned) familiarityEarned.push(...wordScore.familiarityEarned);
    if (wordScore.simplicityEarned) simplicityEarned.push(...wordScore.simplicityEarned);
    if (wordScore.bonusEarned) bonusEarned.push(...wordScore.bonusEarned);
  }
  
  // Average the scores
  const wordCount = words.length;
  const avgFluency = Math.round(totalFluency / wordCount);
  const avgFamiliarity = Math.round(totalFamiliarity / wordCount);
  const avgSimplicity = Math.round(totalSimplicity / wordCount);
  const avgBonus = Math.round(totalBonus / wordCount);
  
  // Total score with some domain-level adjustment
  let total = avgFluency + avgFamiliarity + avgSimplicity + avgBonus;
  
  // Adjust score for multi-word domains
  if (words.length <= 3) {
    total += 5;
    bonusEarned.push(`Good word count: ${words.length} words (+5)`);
  } else {
    total -= (words.length - 3) * 5;
    bonusEarned.push(`Too many words: ${words.length} words (-${(words.length - 3) * 5})`);
  }
  
  return {
    fluency: avgFluency,
    familiarity: avgFamiliarity,
    simplicity: avgSimplicity,
    bonus: avgBonus,
    total: Math.min(100, Math.max(0, total)),
    fluencyEarned: [...new Set(fluencyEarned)],
    familiarityEarned: [...new Set(familiarityEarned)],
    simplicityEarned: [...new Set(simplicityEarned)],
    bonusEarned: [...new Set(bonusEarned)],
    words
  };
}

// Get the score tier and emoji
export function getSpeakabilityTier(score: number): { tier: string; emoji: string } {
  if (score >= 90) return { tier: 'Perfect', emoji: '🎯' };
  if (score >= 80) return { tier: 'Exceptional', emoji: '✨' };
  if (score >= 70) return { tier: 'Smooth', emoji: '🗣️' };
  if (score >= 60) return { tier: 'Good', emoji: '👌' };
  if (score >= 50) return { tier: 'Acceptable', emoji: '🔉' };
  if (score >= 40) return { tier: 'Challenging', emoji: '⚠️' };
  return { tier: 'Difficult', emoji: '🙊' };
}
